<template>
  <template v-if="config">
    <LargeBanner v-if="variant === 'large'" v-bind="bannerVariantProps" />

    <MediumBanner v-if="variant === 'medium'" v-bind="bannerVariantProps" />

    <SmallBanner
      v-if="variant === 'small'"
      v-bind="bannerVariantProps"
      @actioned="emit('actioned')"
    />
  </template>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useDataConsent } from '@backmarket/nuxt-module-tracking/useDataConsent'

import { bannerConfig } from '~/scopes/lead-collection/components/AppStoreDownloadBanner/AppStoreDownloadBanner.constants'

import translations from './AppStoreDownloadBanner.translations'
import type {
  AppStoreDownloadBannerProps,
  BannerVariantProps,
} from './AppStoreDownloadBanner.types'
import LargeBanner from './Variants/LargeBanner.vue'
import MediumBanner from './Variants/MediumBanner.vue'
import SmallBanner from './Variants/SmallBanner.vue'

const logger = useLogger()
const i18n = useI18n()
const route = useRoute()
const { isAppsFlyerUserConsentGiven } = useDataConsent()
const emit = defineEmits<{ actioned: [value: void] }>()

const { variant = 'medium' } = defineProps<AppStoreDownloadBannerProps>()

function ratingValue() {
  const ratingTranslation = i18n(translations.ratingValue)

  if (
    ratingTranslation.trim() !== '' &&
    Number.isFinite(Number(ratingTranslation))
  ) {
    return Number(ratingTranslation)
  }

  logger.error('[AppStoreDownloadBanner] Invalid rating value', {
    ratingTranslation,
    owners: ['bot-squad-content-front', 'bot-squad-lifecycle-front'],
  })

  return undefined
}

const config = computed(() => {
  return bannerConfig[route.name as string]
})

const bannerVariantProps = computed<BannerVariantProps>(() => {
  return {
    consentGiven: isAppsFlyerUserConsentGiven.value,
    title: config.value.title,
    mobileTitle: config.value.mobileTitle,
    subtitle: config.value.subtitle,
    qrCodePath: config.value.qrCodePath,
    rating: ratingValue(),
    desktopTrackedUrl: config.value.desktopTrackedUrl,
    mobileTrackedUrl: config.value.mobileTrackedUrl,
  }
})
</script>
